/** Shaping for the Audience tab. Every number comes from the API; nothing is re-derived as a rate. */
import type { AudienceResp } from '../api/visitorsDesk'
import { fmtInt } from './format'

export interface HeatCell { text: string; opacity: number }

/** One tinted cell per count. Opacity is relative to the largest cell; the text is always the count. */
export function heatCells(cells: number[][]): HeatCell[][] {
  const max = Math.max(0, ...cells.flat())
  return cells.map((row) => row.map((n) => ({ text: fmtInt(n), opacity: max > 0 ? n / max : 0 })))
}

export interface WeekBar {
  weekStart: string
  incomplete: boolean
  parts: { label: string; value: number; tone: 'cyan' | 'amber' }[]
  total: number
}

/** New and returning people per week. The unfinished last week is flagged, never compared with a finished one. */
export function weekBars(weeks: AudienceResp['new_vs_returning']['weeks']): WeekBar[] {
  return weeks.map((w) => ({
    weekStart: w.week_start,
    incomplete: w.incomplete,
    parts: [
      { label: 'Returning', value: w.returning, tone: 'cyan' as const },
      { label: 'New', value: w.new, tone: 'amber' as const },
    ],
    total: w.new + w.returning,
  }))
}

export interface LocationRow { key: string; label: string; people: number; share: number | null; nested: boolean }

/** Countries by people, with US and Canadian regions nested under their country as `· California (US-CA)`. */
export function locationRows(loc: AudienceResp['locations']): LocationRow[] {
  const out: LocationRow[] = []
  const used = new Set<number>()
  const regionRow = (r: AudienceResp['locations']['regions'][number]): LocationRow => ({
    key: `r:${r.country}:${r.region}`,
    label: `· ${r.region}${r.code ? ` (${r.code})` : ''}`,
    people: r.people, share: r.share, nested: true,
  })
  for (const c of loc.countries) {
    out.push({ key: `c:${c.code}`, label: c.label, people: c.people, share: c.share, nested: false })
    loc.regions.forEach((r, i) => {
      if (r.country === c.code) { out.push(regionRow(r)); used.add(i) }
    })
  }
  loc.regions.forEach((r, i) => { if (!used.has(i)) out.push(regionRow(r)) })
  return out
}
